/* eslint-disable import/no-cycle */
/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import numeral from 'numeral';
import { Divider, Checkbox, Form, Button, Row } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import SummaryModalQuote from './SummaryModalQuote';
import { routes } from '../../utils/constants/routes';
import useQuotesContext from './QuotesContext';
import { activityTypeLabelMap } from './rcpmkFields';
import { MRIWK_MRI_BUILDING_TYPES } from './mriwkFields';

const StepModalOne = ({ quote }) => {
  const { t } = useTranslation();
  const {
    quote_number,
    customer,
    type,
    local,
    fraction_type,
    payment_type,
    commission_fee,
    broker_fee,
    remuneration,
    paperwork_fee,
    activities,
    turnover,
    employees_number,
    representatives_number,
    yearly_events,
    total_members,
    buildings,
    package: pckg,
    capital,
    recipient_clause,
    recipient,
    exemption_link,
    activity_type
  } = quote || {};
  const { setVisible } = useQuotesContext();

  const contractWithSupplier = ['MRPWK', 'MRPCM', 'MRPML'];

  const formMentionLayout = {
    wrapperCol: {
      span: 40,
      offset: 0
    }
  };
  const getAssoParam = () => {
    if (local) return '?assoT=localRc';
    return '?assoT=rc';
  };

  const getMRPParam = () => {
    const suffix = `${
      contractWithSupplier.includes(type)
        ? `&sup=${type.slice(-2).toLowerCase()}`
        : ''
    }`;
    if (activities[0] === 'Activité de bureau') {
      return `?mrpT=office${suffix}`;
    }
    return `?mrpT=any${suffix}`;
  };

  const getPrefix = (product) => {
    let prefix = `_${product}`;
    if (product === 'asso') {
      prefix = local ? '_local_asso' : '_rc';
      if (quote?.termination_reason_by_company === 'TERMINATION_NO_PAYMENT')
        prefix += '_certificate_claim';
    } else if (product?.startsWith('mrp')) {
      prefix =
        activities && activities[0] === 'Activité de bureau' ? '_mrb' : '_mrp';
      const ownsOfAllSites =
        !quote?.sites.filter((s) => s.resident_status !== 'OWNER').length > 0;
      if (ownsOfAllSites) prefix += '_owner';
      if (type === 'MRPML') {
        prefix = '_ml';
      }
    } else if (product?.startsWith('rcp')) {
      prefix = '';
      if (type === 'RCPML') {
        prefix = '_ml';
      }
    }
    if (quote?.previous_contract) prefix += '_amendment';
    return prefix;
  };

  const getDocumentCheckLabel = () => {
    return t(
      `validate_quote.modal.document_certification${getPrefix(
        type?.substring(0, 4).toLowerCase()
      )}`
    );
  };

  const getBasePath = (typePath, excludeMRIWK = true) => {
    if (
      (typePath?.toLowerCase().includes('wk') &&
        (!excludeMRIWK || typePath !== 'MRIWK')) ||
      typePath?.toLowerCase().includes('cm') ||
      typePath?.toLowerCase().includes('ml')
    ) {
      return typePath?.substring(0, 3).toLowerCase();
    }
    return typePath?.toLowerCase();
  };

  const getEditPath = (q) => (q?.previous_contract ? 'edit_amendment' : 'edit');

  const getQueryParam = (typeQuery) => {
    if (typeQuery === 'ASSOCIATION') {
      return getAssoParam();
    }
    if (contractWithSupplier.includes(typeQuery)) {
      return getMRPParam();
    }
    if (['RCPWK', 'RCPCM', 'RCPML', 'MRPML'].includes(typeQuery)) {
      return `?sup=${typeQuery.slice(-2).toLowerCase()}`;
    }
    return '';
  };

  const formattedQuote = useMemo(() => {
    const updatedFormattedQuote = {
      ...quote
    };
    const quoteType = type?.toLowerCase();

    Object.entries(updatedFormattedQuote).forEach(([key, value]) => {
      if (typeof value === 'number') {
        updatedFormattedQuote[key] = numeral(value).format('0,0');
      } else if (typeof value === 'boolean') {
        updatedFormattedQuote[key] = value ? 'Oui' : 'Non';
      } else if (value instanceof Date) {
        updatedFormattedQuote[key] = moment(value).format('DD/MM/YYYY');
      } else if (Array.isArray(value)) {
        updatedFormattedQuote[key] = value
          .map((v) =>
            t(`contracts.show.${quoteType}.options.${key}.${v}`, {
              defaultValue: v
            })
          )
          .join(', ');
      } else if (typeof value === 'string') {
        updatedFormattedQuote[key] = t(
          `contracts.show.${quoteType}.options.${key}.${value}`,
          {
            defaultValue: value
          }
        );
      } else {
        updatedFormattedQuote[key] = value;
      }
    });

    return updatedFormattedQuote;
  }, [quote]);

  return (
    <Form.Item noStyle>
      <p style={{ marginTop: 20, textAlign: 'center', fontSize: 20 }}>
        {t('validate_quote.modal.title')}
      </p>
      <Divider orientation="left">
        {t('validate_quote.modal.customer_info')}
      </Divider>
      <p style={{ textAlign: 'justify' }}>
        {`Vous souhaitez transformer le ${
          quote?.previous_contract
            ? t('validate_quote.modal.quote_amendment')
            : t('validate_quote.modal.quote')
        } ${quote_number} pour ${
          type !== 'PREVOYANCE' && customer?.company_name
            ? `${customer.company_name} - `
            : ''
        }${customer?.first_name} ${
          customer?.last_name
        }, avant de valider ce contrat nous vous invitons à contrôler : `}
      </p>
      <ul>
        <li>
          <span>Le n° de téléphone portable de votre client(e) :</span>
          <span style={{ fontWeight: 'bold', fontSize: 16 }}>
            {` ${customer?.phone_number ? customer?.phone_number : '-'} `}
          </span>
        </li>
        <li>
          <span>L&apos; adresse mail de votre client :</span>
          <span style={{ fontWeight: 'bold', fontSize: 16 }}>
            {` ${customer?.email ? customer?.email : '-'} `}
          </span>
        </li>
      </ul>
      {/* {type !== 'RCNGAWK' && (
        <Row justify="center">
          <Link
            to={`${routes.QUOTES}/${getBasePath(type, false)}/${getEditPath(
              quote
            )}/${quote?._id}${getQueryParam(type)}`}
          >
            <Button
              type="primary"
              icon={<EditOutlined />}
              disabled
              onClick={() => setVisible(false)}
            >
              {t('buttons.rectify')}
            </Button>
          </Link>
        </Row>
      )} */}
      {type?.includes('RCP') && !['RCPMK', 'RCPHX'].includes(type) && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {t('validate_quote.modal.rcp_offer')}
          </p>
          <ul>
            {activities &&
              activities.map((element) => <li key={element}>{element}</li>)}
          </ul>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {employees_number
              ? `Le chiffre d'affaires HT déclaré est de ${turnover} € HT/an, le nombre de salariés (hors dirigeant(s)) est de ${employees_number}.`
              : `Le chiffre d'affaires HT déclaré est de ${turnover} € HT/an.`}
          </p>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.warning')}
          </p>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.assurmax_info')}
          </p>
        </>
      )}
      {type === 'RCNGAWK' && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {t('validate_quote.modal.rcp_offer')}
          </p>
          <ul>
            {activities &&
              activities.map((element) => <li key={element}>{element}</li>)}
          </ul>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {`Le chiffre d'affaires HT déclaré est de ${turnover} € HT/an.`}
          </p>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.warning')}
          </p>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.assurmax_info')}
          </p>
        </>
      )}
      {type === 'MRIML' && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {t('validate_quote.modal.mri_offer')}
          </p>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {`L'adresse du bien à assurer est : ${
              buildings[0]?.address?.formatted_address
                ? buildings[0]?.address?.formatted_address
                : '-'
            }.`}
          </p>
          <ul>
            <li>
              {`Le siret de l'immeuble est :  ${
                buildings[0].building_siret ? buildings[0].building_siret : '-'
              }`}
            </li>
            <li>
              {`Le nom de l'immeuble est :  ${
                buildings[0].building_name ? buildings[0].building_name : '-'
              }`}
            </li>
            <li>{`Sa superficie est :  ${quote?.surface_area || '-'} M2`}</li>
            <li>
              {`Son année de construction est environ :  ${
                quote.construction_year
                  ? `${moment(quote.construction_year).format('YYYY')}`
                  : '-'
              }`}
            </li>
            <li>
              {`Le contrat devra couvrir ${
                quote.total_buildings
              } bâtiment, ce bâtiment à assurer est un immeuble à usage ${t(
                `contracts.tags.${quote.main_use}`
              )}, ${t(`contracts.tags.${quote.risk_typology}`)}.`}
            </li>
            <li>
              {`Le souscripteur déclare la présence d'un plancher béton : ${
                quote.concrete_floor
                  ? t(
                      `quotes.form.switch.${
                        quote.concrete_floor ? 'yes' : 'no'
                      }`
                    )
                  : 'Non'
              } et déclare que le risque est  occupé : ${
                quote.empty_unoccupied_works
                  ? t(
                      `quotes.form.switch.${
                        quote.empty_unoccupied_works ? 'yes' : 'no'
                      }`
                    )
                  : 'Oui'
              } `}
            </li>
            <li>
              {`Le souscripteur est une  ${
                quote.subscriber ? t(`contracts.tags.${quote.subscriber}`) : '-'
              }`}
            </li>
            <li>
              {`L'adresse postale est ${customer?.company_name ||
                ' '},  ${customer?.address?.number || ' '}, ${customer?.address
                ?.street || ' '},
              ${customer?.address?.postal_code || ' '},
              ${customer?.address?.city || ' '}  `}
            </li>
            <li>
              {`Le souscripteur agit en tant que   ${
                quote.subscriber_type
                  ? t(`contracts.tags.${quote.subscriber_type}`)
                  : '-'
              }`}
            </li>
          </ul>
          <p
            style={{
              fontStyle: 'italic',

              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.warning')}
          </p>
          <p
            style={{
              fontStyle: 'italic',

              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.assurmax_info')}
          </p>
        </>
      )}
      {type === 'PNOML' && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {t('validate_quote.modal.mri_offer')}
          </p>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {`L'adresse du bien à assurer est : ${
              buildings[0]?.address?.formatted_address
                ? buildings[0]?.address?.formatted_address
                : '-'
            }.`}
          </p>
          <ul>
            <li>
              {`Le siret de l'immeuble est :  ${
                buildings[0].building_siret ? buildings[0].building_siret : '-'
              }`}
            </li>
            <li>
              {`Le nom de l'immeuble est :  ${
                buildings[0].building_name ? buildings[0].building_name : '-'
              }`}
            </li>
            <li>{`Sa superficie est :  ${quote?.surface_area || '-'} M2`}</li>
            <li>
              {`Le contrat devra couvrir ${
                quote.total_buildings
              } bâtiment, ce bâtiment à assurer est un immeuble à usage ${t(
                `contracts.tags.${quote.main_use}`
              )}.`}
            </li>
            <li>
              {`Le souscripteur est une  ${
                quote.subscriber ? t(`contracts.tags.${quote.subscriber}`) : '-'
              }`}
            </li>
            <li>
              {`L'adresse postale est ${customer?.company_name ||
                ' '},  ${customer?.address?.number || ' '}, ${customer?.address
                ?.street || ' '},
              ${customer?.address?.postal_code || ' '},
              ${customer?.address?.city || ' '}  `}
            </li>
            <li>
              {`Le souscripteur agit en tant que   ${
                quote.subscriber_type
                  ? t(`contracts.tags.${quote.subscriber_type}`)
                  : '-'
              }`}
            </li>
          </ul>
          <p
            style={{
              fontStyle: 'italic',

              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.warning')}
          </p>
          <p
            style={{
              fontStyle: 'italic',

              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.assurmax_info')}
          </p>
        </>
      )}
      {type === 'ASSOCIATION' && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {local
              ? t('validate_quote.modal.asso_offer_local')
              : t('validate_quote.modal.asso_offer')}
          </p>
          <ul>
            {activities &&
              activities.map((element) => <li key={element}>{element}</li>)}
          </ul>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {`Le budget de fonctionnement HT déclaré est de ${numeral(
              turnover
            ).format(
              '0,0'
            )} € HT/an, le nombre de dirigeants est de ${representatives_number ||
              0}, le nombre de membres du bureau est de ${employees_number ||
              0}, le nombre de licenciés est de ${total_members ||
              0}, le nombre de manifestation par an est de ${yearly_events ||
              0}`}
          </p>
          <p
            style={{
              fontStyle: 'italic',

              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.warning')}
          </p>
          <p
            style={{
              fontStyle: 'italic',

              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.assurmax_info')}
          </p>
        </>
      )}
      {type?.includes('MRP') && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {t('validate_quote.modal.mrp_offer')}
          </p>
          <ul>
            {activities &&
              activities.map((element) => <li key={element}>{element}</li>)}
          </ul>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {`Le chiffre d'affaires HT déclaré est de ${numeral(
              turnover
            ).format(
              '0,0'
            )} € HT/an, le nombre de salarié(s) (hors dirigeant(s)) est de ${employees_number}.`}
          </p>
          <p
            style={{
              fontStyle: 'italic',

              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.warning')}
          </p>
          <p
            style={{
              fontStyle: 'italic',

              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.assurmax_info')}
          </p>
        </>
      )}
      {type === 'PREVOYANCE' && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {t(`validate_quote.modal.prevoyance.${pckg}`)}
          </p>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {t(`validate_quote.modal.prevoyance.${capital}`)}
          </p>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            Votre client(e) a souscrit aux garanties suivantes dans le cadre de
            sa prévoyance accident
          </p>
          <ul>
            <li>Garantie en cas de décès</li>
            <li>
              {`Garantie en cas d'invalidité permanente partielle ou totale
                (franchise 10%)`}
            </li>
            <li>Protection juridique (offerte)</li>
          </ul>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {t(`validate_quote.modal.prevoyance.${recipient_clause}`)}
          </p>
          <p style={{ fontStyle: 'italic', textAlign: 'justify' }}>
            {t(
              `validate_quote.modal.prevoyance.${
                recipient_clause === 'STANDARD'
                  ? 'standard_clause'
                  : 'custom_clause'
              }`,
              {
                recipient: {
                  ...recipient,
                  dob: recipient && moment(recipient.dob).format('LL')
                }
              }
            )}
          </p>
        </>
      )}
      {type === 'DO' && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {t('validate_quote.modal.do_offer')}
          </p>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {t('validate_quote.modal.do_customer_turnover', {
              turnover: numeral(turnover).format()
            })}
          </p>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.warning')}
          </p>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.assurmax_info')}
          </p>
        </>
      )}
      {type === 'RCPMK' && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <Trans>
            <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
              {t('validate_quote.modal.rcpmk_offer', {
                activity_type: activityTypeLabelMap[activity_type]
              })}
            </p>
          </Trans>
          <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
            {t('validate_quote.modal.do_customer_turnover', {
              turnover: numeral(turnover).format()
            })}
          </p>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.warning')}
          </p>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.assurmax_info')}
          </p>
        </>
      )}
      {type === 'RCPHX' && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <Trans>
            <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
              {t('validate_quote.modal.rcphx_offer', formattedQuote)}
            </p>

            <p>{t('validate_quote.modal.rcphx.turnover', formattedQuote)}</p>
            <p>
              {t('validate_quote.modal.rcphx.guarantee_limit', formattedQuote)}
            </p>
            {!['MISC','BIE','MED','OSTEO'].includes(quote.activity_type) && (
              <p>
                {t(
                  'validate_quote.modal.rcphx.turnover_usa_canada',
                  formattedQuote
                )}
              </p>
            )}
          </Trans>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.warning')}
          </p>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.assurmax_info')}
          </p>
        </>
      )}
      {type === 'RCGLS' && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <Trans>
            <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
              {t('validate_quote.modal.rcpgls_offer', formattedQuote)}
            </p>

            <p>{t('validate_quote.modal.rcphx.turnover', formattedQuote)}</p>
            <p>
              {t('validate_quote.modal.rcphx.guarantee_limit', formattedQuote)}
            </p>
            {(() => {
              const includedOptionsByActivity =
                {
                  COA: [
                    'insurance_brokers',
                    'operational_liability',
                    'iobsp_broker_errors',
                    'iobsp_exclusive_agent_errors',
                    'iobsp_non_exclusive_agent_errors',
                    'iobsp_mandate_professional',
                    'banking_solicitor_errors'
                  ],
                  MIA: ['operational_liability', 'iobsp_mandate_professional'],
                  IOBSP: [
                    'operational_liability',
                    'insurance_sub_agents_errors'
                  ],
                  MIOBSP: [
                    'operational_liability',
                    'insurance_sub_agents_errors',
                    'banking_and_finance_intermediary_professional',
                    'borrower_insurance_intermediary_errors'
                  ],
                  CGP_CIF: [
                    'operational_liability',
                    'real_estate_transaction_guarantee'
                  ],
                  AI: [
                    'operational_liability',
                    'real_estate_transaction_errors'
                  ],
                  ACI: [
                    'operational_liability',
                    'real_estate_transaction_errors'
                  ]
                }[formattedQuote.main_activity] || [];

              const options =
                {
                  COA: [
                    'investment_advisor_errors',
                    'insurance_intermediary_guarantee',
                    'legal_protection',
                    'extended_legal_protection'
                  ],
                  MIA: [
                    'insurance_intermediary_guarantee',
                    'iobsp_mandate_professional',
                    'legal_protection',
                    'extended_legal_protection'
                  ],
                  IOBSP: [
                    'insurance_intermediary_guarantee',
                    'iobsp_broker_guarantee',
                    'banking_solicitor_errors',
                    'legal_protection',
                    'extended_legal_protection'
                  ],
                  MIOBSP: [
                    'insurance_intermediary_guarantee',
                    'insurance_sub_agents_errors',
                    'legal_protection',
                    'extended_legal_protection'
                  ],
                  CGP_CIF: [
                    'investment_advisor_errors',
                    'legal_protection',
                    'extended_legal_protection'
                  ],
                  AI: [
                    'iobsp_broker_errors',
                    'iobsp_exclusive_agent_errors',
                    'iobsp_non_exclusive_agent_errors',
                    'iobsp_mandate_professional',
                    'insurance_intermediary_guarantee',
                    'general_agents_and_brokers_errors',
                    'insurance_sub_agents_errors',
                    'real_estate_transaction_guarantee',
                    'legal_protection',
                    'extended_legal_protection'
                  ],
                  ACI: ['legal_protection', 'extended_legal_protection']
                }[formattedQuote.main_activity] || [];

              const included = includedOptionsByActivity.filter(
                (key) => quote?.[key] === true
              );
              const selected = options.filter((key) => quote?.[key] === true);
              const unselected = options.filter((key) => quote?.[key] !== true);

              return (
                <>
                  {included.length > 0 && (
                    <>
                      <p style={{ fontWeight: 'bold' }}>
                        {t('validate_quote.modal.included')}
                      </p>
                      <ul style={{ marginBottom: '1rem' }}>
                        {included.map((key) => (
                          <li key={key}>{t(`contracts.form.${key}`)}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {selected.length > 0 && (
                    <>
                      <p style={{ fontWeight: 'bold' }}>
                        {t('validate_quote.modal.selected')}
                      </p>
                      <ul style={{ marginBottom: '1rem' }}>
                        {selected.map((key) => (
                          <li key={key}>{t(`contracts.form.${key}`)}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {unselected.length > 0 && (
                    <>
                      <p style={{ fontWeight: 'bold' }}>
                        {t('validate_quote.modal.guarantees_not_selected')}
                      </p>
                      <ul>
                        {unselected.map((key) => (
                          <li key={key}>{t(`contracts.form.${key}`)}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              );
            })()}
          </Trans>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.warning')}
          </p>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.assurmax_info')}
          </p>
        </>
      )}
      {type === 'MRIWK' && (
        <>
          <Divider orientation="left">
            {t('contracts.show.general_info')}
          </Divider>
          <Trans>
            <p style={{ fontWeight: 'bold', textAlign: 'justify' }}>
              {t('validate_quote.modal.mriwk_offer', {
                count: 1,
                context: MRIWK_MRI_BUILDING_TYPES.includes(quote?.building_type)
                  ? 'mri'
                  : 'pno'
              })}
            </p>
          </Trans>

          <Trans>
            <p>
              {t('validate_quote.modal.mriwk.property_address', {
                address: `${quote.address.number} ${quote.address.street}, ${quote.address.postal_code} ${quote.address.city}`
              })}
            </p>
            <p>
              {t('validate_quote.modal.mriwk.property_type', formattedQuote)}
            </p>
            <p>
              {t('validate_quote.modal.mriwk.property_quality', formattedQuote)}
            </p>
            <p>
              {t('validate_quote.modal.mriwk.property_surface', formattedQuote)}
            </p>
            {/* <p>
              {t('validate_quote.modal.mriwk.property_nature', formattedQuote)}
            </p> */}
            {quote.contains_professional_activity ? (
              <p>
                {t(
                  'validate_quote.modal.mriwk.contains_professional_activity',
                  formattedQuote
                )}
              </p>
            ) : (
              <p>
                {t(
                  'validate_quote.modal.mriwk.not_contains_professional_activity',
                  formattedQuote
                )}
              </p>
            )}
            {quote.is_vacant ? (
              <p>{t('validate_quote.modal.mriwk.is_vacant', formattedQuote)}</p>
            ) : (
              <p>
                {t('validate_quote.modal.mriwk.is_not_vacant', formattedQuote)}
              </p>
            )}

            <p>
              {t(
                `validate_quote.modal.mriwk.is_furnished.${quote?.is_furnished}`,
                quote
              )}
            </p>

            {/* {quote.is_igh_igs ? (
              <p>
                {t('validate_quote.modal.mriwk.is_igh_igs', formattedQuote)}
              </p>
            ) : (
              <p>
                {t('validate_quote.modal.mriwk.is_not_igh_igs', formattedQuote)}
              </p>
            )} */}

            {/* <p>{t('validate_quote.modal.mriwk.wood_floor', formattedQuote)}</p> */}
            {/* <p>
              {t('validate_quote.modal.mriwk.property_altitude', {
                ...formattedQuote,
                altitude: formattedQuote?.property_altitude?.replace('<', '-')
              })}
            </p>
            <p>
              {t(
                'validate_quote.modal.mriwk.property_managers',
                formattedQuote
              )}
            </p> */}
          </Trans>

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.warning')}
          </p>
          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'justify'
            }}
          >
            {t('validate_quote.modal.assurmax_info')}
          </p>
        </>
      )}
      <SummaryModalQuote quote={quote} formattedQuote={formattedQuote} />
      {!exemption_link && type !== 'RCNGAWK' && (
        <Row justify="center">
          <Link
            to={`${routes.QUOTES}/${getBasePath(type)}/${getEditPath(quote)}/${
              quote?._id
            }${getQueryParam(type)}`}
          >
            <Button type="primary" onClick={() => setVisible(false)}>
              <EditOutlined />
              {`${t('buttons.rectify')} `}
            </Button>
          </Link>
        </Row>
      )}
      <Divider orientation="left">
        {t('validate_quote.modal.remuneration_fees')}
      </Divider>
      <ul style={{ color: 'var(--clientColor)' }}>
        <li>
          {`Le fractionnement choisi par l'assuré est ${t(
            `contracts.form.options.${fraction_type}`
          )} par ${t(`contracts.form.options.${payment_type}`)}`}
        </li>
        <li>
          {`Vous avez choisi un taux de commission variable de `}
          <strong>{` ${commission_fee}% `}</strong>
          {` ainsi que  `}
          <strong>{` ${broker_fee} `}</strong>
          {`  € de frais de courtage mensuel`}
        </li>
        <li>
          {`Votre rémunération annuelle pour ce contrat sera donc de `}
          <strong>{` ${(remuneration * 12)?.toFixed(2)} `}</strong>
          {` € (soit ${remuneration?.toFixed(2)} €/mois)`}
        </li>
        {paperwork_fee ? (
          <li>
            {`Vous avez également choisi de percevoir la somme de `}
            <strong>{` ${paperwork_fee} `}</strong>
            {` € au titre des frais de dossier`}
          </li>
        ) : (
          ''
        )}
      </ul>
      {!exemption_link && type !== 'RCNGAWK' && (
        <Row justify="center" style={{ marginBottom: 25 }}>
          <Link
            to={`${routes.QUOTES}/${getBasePath(type)}/${getEditPath(quote)}/${
              quote?._id
            }${getQueryParam(type)}`}
          >
            <Button type="primary" onClick={() => setVisible(false)}>
              <EditOutlined />
              {`${t('buttons.rectify')} `}
            </Button>
          </Link>
        </Row>
      )}
      <p style={{ fontStyle: 'italic', textAlign: 'justify' }}>
        {commission_fee + broker_fee <= 20 &&
          t(`validate_quote.modal.remuneration_1`)}
        {commission_fee + broker_fee >= 21 &&
          commission_fee + broker_fee <= 36 &&
          t(`validate_quote.modal.remuneration_2`)}
        {commission_fee + broker_fee >= 37 &&
          t(`validate_quote.modal.remuneration_3`)}
      </p>
      <p style={{ fontStyle: 'italic', textAlign: 'justify' }}>
        {paperwork_fee <= 25 && t(`validate_quote.modal.administration_fees_1`)}
        {paperwork_fee >= 26 &&
          paperwork_fee <= 75 &&
          t(`validate_quote.modal.administration_fees_2`)}
        {paperwork_fee >= 76 && t(`validate_quote.modal.administration_fees_3`)}
      </p>
      <Form.Item
        {...formMentionLayout}
        name={['document_certification']}
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) =>
              value
                ? Promise.resolve()
                : Promise.reject(t('errors.form.REQUIRED'))
          }
        ]}
      >
        <Checkbox>{getDocumentCheckLabel()}</Checkbox>
      </Form.Item>
    </Form.Item>
  );
};

StepModalOne.propTypes = {
  quote: PropTypes.shape({
    insurer: PropTypes.object,
    previous_contract: PropTypes.string,
    type: PropTypes.string,
    activity_type: PropTypes.string,
    quote_number: PropTypes.string,
    customer: PropTypes.shape({
      company_name: PropTypes.string,
      last_name: PropTypes.string,
      first_name: PropTypes.string,
      phone_number: PropTypes.string,
      email: PropTypes.string,
      _id: PropTypes.string,
      address: PropTypes.shape({
        number: PropTypes.string,
        street: PropTypes.string,
        additional: PropTypes.string,
        postal_code: PropTypes.string,
        city: PropTypes.string
      })
    }),
    fraction_type: PropTypes.string,
    payment_type: PropTypes.string,
    termination_reason_by_company: PropTypes.string,
    sites: PropTypes.array,
    commission_fee: PropTypes.number,
    broker_fee: PropTypes.number,
    remuneration: PropTypes.number,
    paperwork_fee: PropTypes.number,
    activities: PropTypes.arrayOf(PropTypes.string),
    turnover: PropTypes.number,
    employees_number: PropTypes.number,
    representatives_number: PropTypes.number,
    total_members: PropTypes.number,
    yearly_events: PropTypes.number,
    buildings: PropTypes.arrayOf(
      PropTypes.shape({
        address: PropTypes.shape({
          formatted_address: PropTypes.string
        }),
        building_siret: PropTypes.string,
        building_name: PropTypes.string
      })
    ),
    surface_area: PropTypes.number,
    building_condition: PropTypes.string,
    construction_year: PropTypes.number,
    total_buildings: PropTypes.string,
    main_use: PropTypes.string,
    building_category: PropTypes.string,
    risk_typology: PropTypes.string,
    concrete_floor: PropTypes.bool,
    empty_unoccupied_works: PropTypes.bool,
    subscriber: PropTypes.string,
    subscriber_type: PropTypes.string,
    package: PropTypes.string,
    capital: PropTypes.number,
    recipient_clause: PropTypes.string,
    recipient: PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      dob: PropTypes.string
    }),
    _id: PropTypes.string
  }).isRequired,
  formModalValues: PropTypes.shape({
    duty_of_advice: PropTypes.string,
    compliant_offer: PropTypes.bool
  })
};

StepModalOne.defaultProps = {
  formModalValues: null
};

export default StepModalOne;
